import type { ActivityEvidenceRun } from './activity-spine.ts';
import type { AppKeyRole } from './app-objects.ts';
import type { TaskInlineCapabilityKey } from './capabilities.ts';
import type { ToolHandoffOrigin } from './continuations.ts';
import type { ConnectionRequirement } from './integrations.ts';
import type {
  ContextualActionSpec,
  ToolActionKey,
  ToolActionOutcomeSpec,
} from './tool-actions.ts';

export const RECIPE_VIEW_TYPES = [
  'artifact_document',
  'score_card',
  'action_list',
  'metric_grid',
  'health_gauge',
  'entity_summary',
  'data_table',
  'chart',
  'timeline',
] as const;

export const VIEW_TYPES = [...RECIPE_VIEW_TYPES, 'fallback_panel'] as const;

export const VIEW_PATTERN_KEYS = [
  'artifact_document',
  'score_card',
  'action_list',
  'metric_grid',
  'health_gauge',
  'entity_summary',
  'data_table',
  'chart',
  'timeline',
  'fallback_empty',
] as const;

export const VIEW_ROLE_KEYS = ['informative', 'actionable', 'communicative'] as const;

export const VIEW_ICON_KEYS = [
  'document',
  'score',
  'actions',
  'metrics',
  'health',
  'entity',
  'table',
  'chart',
  'timeline',
  'empty',
] as const;

export const DASHBOARD_VIEW_FALLBACK_REASONS = [
  'source_plan_missing',
  'source_no_data',
  'source_error',
  'source_dispatch_unsupported',
  'preview_invalid',
] as const;

export const VIEW_RENDER_FALLBACK_REASONS = [
  'pattern_missing',
  'pattern_unregistered',
  'view_payload_missing',
  'view_payload_invalid',
  'view_payload_mismatch',
  'dashboard_view_payload_missing',
  'ad_hoc_view_payload_invalid',
] as const;

export const DEFAULT_VIEW_SCHEMA_VERSION = '1.0.0';

export type FlowViewType = (typeof RECIPE_VIEW_TYPES)[number];
export type ViewType = (typeof VIEW_TYPES)[number];
export type ViewPatternKey = (typeof VIEW_PATTERN_KEYS)[number];
export type ViewRole = (typeof VIEW_ROLE_KEYS)[number];
export type ViewIconKey = (typeof VIEW_ICON_KEYS)[number];
export type ViewFallbackPatternKey = 'fallback_empty';
export type DashboardViewFallbackReason =
  (typeof DASHBOARD_VIEW_FALLBACK_REASONS)[number];
export type ViewRenderFallbackReason = (typeof VIEW_RENDER_FALLBACK_REASONS)[number];
export type FallbackPanelReason = DashboardViewFallbackReason | ViewRenderFallbackReason;

export interface ViewDisplayMeta {
  title?: string;
  description?: string;
  icon?: ViewIconKey;
  tags?: string[];
}

export interface ViewInteractionPolicy {
  role: ViewRole;
  allowsRefresh: boolean;
  allowsPrimaryAction: boolean;
  requiresConfirmation: boolean;
  supportsCommunication: boolean;
}

export type EmptyViewConfig = Readonly<Record<never, never>>;

export interface ViewPatternConfigMap {
  artifact_document: EmptyViewConfig;
  score_card: EmptyViewConfig;
  action_list: EmptyViewConfig;
  metric_grid: EmptyViewConfig;
  health_gauge: EmptyViewConfig;
  entity_summary: EmptyViewConfig;
  data_table: EmptyViewConfig;
  chart: EmptyViewConfig;
  timeline: EmptyViewConfig;
  fallback_empty: EmptyViewConfig;
}

interface BaseViewDefinition<TPatternKey extends ViewPatternKey> {
  id?: string;
  patternKey: TPatternKey;
  schemaVersion: string;
  sourceViewType?: ViewType | null;
  displayMeta: ViewDisplayMeta;
  interactionPolicy: ViewInteractionPolicy;
  fallbackPatternKey: ViewFallbackPatternKey;
}

export type ScoreCardViewDefinition = BaseViewDefinition<'score_card'> & {
  config: ViewPatternConfigMap['score_card'];
};

export type ArtifactDocumentViewDefinition = BaseViewDefinition<'artifact_document'> & {
  config: ViewPatternConfigMap['artifact_document'];
};

export type ActionListViewDefinition = BaseViewDefinition<'action_list'> & {
  config: ViewPatternConfigMap['action_list'];
};

export type MetricGridViewDefinition = BaseViewDefinition<'metric_grid'> & {
  config: ViewPatternConfigMap['metric_grid'];
};

export type HealthGaugeViewDefinition = BaseViewDefinition<'health_gauge'> & {
  config: ViewPatternConfigMap['health_gauge'];
};

export type EntitySummaryViewDefinition = BaseViewDefinition<'entity_summary'> & {
  config: ViewPatternConfigMap['entity_summary'];
};

export type DataTableViewDefinition = BaseViewDefinition<'data_table'> & {
  config: ViewPatternConfigMap['data_table'];
};

export type ChartViewDefinition = BaseViewDefinition<'chart'> & {
  config: ViewPatternConfigMap['chart'];
};

export type TimelineViewDefinition = BaseViewDefinition<'timeline'> & {
  config: ViewPatternConfigMap['timeline'];
};

export type FallbackEmptyViewDefinition = BaseViewDefinition<'fallback_empty'> & {
  config: ViewPatternConfigMap['fallback_empty'];
};

export type ViewDefinition =
  | ArtifactDocumentViewDefinition
  | ScoreCardViewDefinition
  | ActionListViewDefinition
  | MetricGridViewDefinition
  | HealthGaugeViewDefinition
  | EntitySummaryViewDefinition
  | DataTableViewDefinition
  | ChartViewDefinition
  | TimelineViewDefinition
  | FallbackEmptyViewDefinition;

export type ViewDefinitionMap = {
  [K in ViewPatternKey]: Extract<ViewDefinition, { patternKey: K }>;
};

export interface ScoreCardSegment {
  label: string;
  value: number;
  max?: number;
  status: 'good' | 'warning' | 'critical' | 'neutral';
}

export interface ScoreCardFact {
  label: string;
  value: string;
  status?: 'good' | 'warning' | 'critical' | 'neutral';
}

export interface ArtifactDocumentMetaItem {
  label: string;
  value: string;
}

export type ArtifactDocumentBodyFormat = 'plain_text' | 'markdown';
export type ArtifactDocumentExportAction = 'copy_markdown' | 'download_markdown';

export interface ScoreCardAction {
  label: string;
  type: 'primary' | 'secondary';
  href?: string;
  flow_id?: string;
}

export type LightViewAction =
  | {
      type: 'link';
      label: string;
      href: string;
    }
  | {
      type: 'copy_text';
      label: string;
      text: string;
    }
  | {
      type: 'tool_handoff';
      label: string;
      toolKey: string;
      prefill: Record<string, string>;
      origin?: ToolHandoffOrigin;
    }
  | {
      type: 'server_action';
      label: string;
      actionKey: ToolActionKey;
      sourceRunId: string;
      sourceActionId?: string | null;
      sourceActionIndex?: number | null;
      sourceActionLabel?: string | null;
      actionSpec?: ContextualActionSpec | null;
      /** @deprecated Compatibility for persisted CRM action cards. */
      outcomeSpec?: ToolActionOutcomeSpec | null;
      pendingLabel?: string;
      successLabel?: string;
      connectionRequirements?: ConnectionRequirement[];
    }
  | {
      type: 'inline_capability';
      label: string;
      capabilityKey: TaskInlineCapabilityKey;
      prompt: string;
      sourceRunId?: string;
      sourceActionId?: string;
      sourceActionIndex?: number;
      sourceActionLabel?: string;
      connectionRequirements?: ConnectionRequirement[];
    }
  | {
      type: 'flow_handoff';
      label: string;
      seedPrompt: string;
      flowId?: string;
      origin?: ToolHandoffOrigin;
    };

export interface CommunicationCue {
  tone: 'info' | 'warning' | 'success';
  title: string;
  body: string;
}

export interface ArtifactDocumentViewData {
  title: string;
  subject: string;
  body: string;
  body_format?: ArtifactDocumentBodyFormat;
  meta_items: ArtifactDocumentMetaItem[];
  export_actions?: ArtifactDocumentExportAction[];
  primaryAction?: LightViewAction | null;
}

export interface ScoreCardViewData {
  title: string;
  subtitle?: string;
  score?: number;
  score_label?: string;
  trend?: 'up' | 'down' | 'stable';
  trend_delta?: string;
  status: 'good' | 'warning' | 'critical' | 'neutral';
  segments?: ScoreCardSegment[];
  facts?: ScoreCardFact[];
  insight?: string;
  primaryAction?: LightViewAction | null;
  communication?: CommunicationCue | null;
  actions?: ScoreCardAction[];
}

export interface ActionListItem {
  id: string;
  label: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'done' | 'skipped';
  category?: string;
  context?: string;
  due?: string;
  entity?: {
    name: string;
    type: string;
  };
  flow_id?: string;
  itemAction?: LightViewAction | null;
  actionability?: {
    state: 'ready' | 'blocked' | 'advisory';
    label: string;
    detail?: string;
    evidenceRuns?: ActivityEvidenceRun[];
  };
}

export interface ActionListViewData {
  title: string;
  description?: string;
  primaryAction?: LightViewAction | null;
  communication?: CommunicationCue | null;
  items: ActionListItem[];
  summary?: {
    total: number;
    completed: number;
    high_priority: number;
  };
}

export interface MetricGridMetric {
  label: string;
  value: string | number;
  unit?: string;
  trend?: 'up' | 'down' | 'stable';
  trend_value?: string;
  status?: 'good' | 'warning' | 'critical' | 'neutral';
}

export interface MetricGridViewData {
  source_note?: string;
  title?: string;
  metrics: MetricGridMetric[];
  /**
   * cards (default) = one card per metric; band = the headline figures of a
   * result in one light strip, for 2 to 5 key numbers at the top.
   */
  presentation?: 'cards' | 'band';
}

export interface HealthGaugeDimension {
  label: string;
  score: number;
  max?: number;
  weight: number;
  signals: string[];
}

export interface HealthGaugeViewData {
  entity_name: string;
  entity_type: 'account' | 'contact' | 'deal';
  overall_score: number;
  overall_max?: number;
  overall_status: 'healthy' | 'at_risk' | 'critical';
  dimensions: HealthGaugeDimension[];
  last_updated: string;
  recommended_action?: string;
  primaryAction?: LightViewAction | null;
  communication?: CommunicationCue | null;
}

export interface EntitySummaryViewData {
  entity: {
    name: string;
    type: 'account' | 'contact' | 'deal' | 'company';
    avatar_url?: string;
  };
  headline: string;
  key_facts: Array<{
    label: string;
    value: string;
  }>;
  health?: {
    score: number;
    status: 'healthy' | 'at_risk' | 'critical';
  };
  recent_activity?: Array<{
    date: string;
    summary: string;
  }>;
  tags?: string[];
  communication?: CommunicationCue | null;
}

export interface DataTableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  /** What the column designates (L48), set by the code that assembles the table: the name, a domain, a link… */
  role?: Exclude<AppKeyRole, 'crm_company'>;
  /** How the value reads (U2, 02/11/2026), declared by the step that writes the column; otherwise read from the values. */
  format?: DataTableColumnFormat;
}

/**
 * How a column's value reads (U2): the screen draws it accordingly (a date as
 * « il y a 3 j », tags as chips, a figure right-aligned). A role (domain, url,
 * email) says it already; without either, the code reads the values' shape.
 */
export const DATA_TABLE_COLUMN_FORMATS = ['text', 'long_text', 'number', 'money', 'percent', 'date', 'tags', 'rating', 'decision'] as const;
export type DataTableColumnFormat = typeof DATA_TABLE_COLUMN_FORMATS[number];

export type DataTableCellTone = 'good' | 'warning' | 'critical' | 'neutral';

export interface DataTableCell {
  key: string;
  value: string | number | boolean | null;
  /**
   * Reading of the value, shown as a colored dot: good, warning (to check),
   * critical, neutral. Makes a comparison matrix or a selection readable at a
   * glance. Set by the producer, never guessed from the words.
   */
  tone?: DataTableCellTone;
}

export interface DataTableRow {
  id: string;
  cells: DataTableCell[];
}

/** Shared by the view schema and the format given to the planner: the producer knows the bound. */
export const DATA_TABLE_EMPTY_MESSAGE_MAX = 1_000;

export interface DataTableViewData {
  source_note?: string;
  title?: string;
  columns: DataTableColumn[];
  rows: DataTableRow[];
  empty_message?: string;
}

/**
 * Chart (layout lab, lot 2, 03/10/2026): one homogeneous chart drawn by Domino
 * in the charter. line = evolution over time; bar = comparison of categories;
 * stacked_bar = parts of a whole per category; horizontal_bar = ranking with
 * long labels. Values are aligned with `categories` (null = no value).
 */
export type ChartKind = 'line' | 'bar' | 'stacked_bar' | 'horizontal_bar';

/** Shared by the view schema and the format given to the planner. */
export const CHART_MAX_SERIES = 6;
export const CHART_MAX_CATEGORIES = 60;

export interface ChartSeries {
  key: string;
  label: string;
  values: Array<number | null>;
}

export interface ChartViewData {
  title?: string;
  source_note?: string;
  kind: ChartKind;
  categories: string[];
  series: ChartSeries[];
  /** Unit of the values (« € », « % », « jours »). */
  unit?: string;
  /** Reference line, e.g. the objective. */
  target?: { label: string; value: number } | null;
  /** One sentence that reads the chart for the user. */
  insight?: string;
  empty_message?: string;
}

/**
 * Timeline (layout lab, lot 3b, 03/10/2026): dated events of an account, a
 * deal or a plan, in the order given (most recent first for a history, first
 * step first for a plan). Events after today read as planned.
 */
export const TIMELINE_MAX_EVENTS = 100;

export interface TimelineEvent {
  /** ISO date (2026-10-02) or ISO date-time. */
  date: string;
  label: string;
  detail?: string;
  /** Business kind, e.g. « Email », « Rendez-vous », « Étape CRM ». */
  kind?: string;
  tone?: DataTableCellTone;
  url?: string;
}

export interface TimelineViewData {
  title?: string;
  source_note?: string;
  events: TimelineEvent[];
  /** One sentence that reads the timeline for the user. */
  insight?: string;
  empty_message?: string;
}

export interface FallbackPanelViewData {
  title: string;
  message: string;
  reason?: FallbackPanelReason;
  key_values?: Array<{
    key: string;
    value: string | number | boolean | null;
  }>;
  text_excerpt?: string;
  raw_preview_json?: string;
}

export interface ViewPatternDataMap {
  artifact_document: ArtifactDocumentViewData;
  score_card: ScoreCardViewData;
  action_list: ActionListViewData;
  metric_grid: MetricGridViewData;
  health_gauge: HealthGaugeViewData;
  entity_summary: EntitySummaryViewData;
  data_table: DataTableViewData;
  chart: ChartViewData;
  timeline: TimelineViewData;
  fallback_empty: FallbackPanelViewData;
}

export interface ViewDataMap {
  artifact_document: ArtifactDocumentViewData;
  score_card: ScoreCardViewData;
  action_list: ActionListViewData;
  metric_grid: MetricGridViewData;
  health_gauge: HealthGaugeViewData;
  entity_summary: EntitySummaryViewData;
  data_table: DataTableViewData;
  chart: ChartViewData;
  timeline: TimelineViewData;
  fallback_panel: FallbackPanelViewData;
}

export type ViewSchemaVersionMap = {
  [K in ViewType]: string;
};

export type ViewEnvelope<T extends ViewType = ViewType> = {
  view_type: T;
  schema_version: string;
  data: ViewDataMap[T];
};

export type AnyViewEnvelope = {
  [K in ViewType]: ViewEnvelope<K>;
}[ViewType];

export const isViewType = (value: unknown): value is ViewType =>
  typeof value === 'string' && VIEW_TYPES.includes(value as ViewType);

export const isFlowViewType = (value: unknown): value is FlowViewType =>
  typeof value === 'string' && RECIPE_VIEW_TYPES.includes(value as FlowViewType);

export const isViewPatternKey = (value: unknown): value is ViewPatternKey =>
  typeof value === 'string' && VIEW_PATTERN_KEYS.includes(value as ViewPatternKey);
